/* Sväto — fiche produit canapé (svato-pdp-*). Comportements uniquement :
   tout le contenu est rendu en Liquid. Vanilla JS, aucune dépendance.
   Chargé en module (différé, exécuté une seule fois même si plusieurs sections l'appellent). */

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
/* Zone d'achat : notre section (svato-pdp-main, [data-svato-main]) ou, depuis le 21/09/2026,
   la section produit native FullStack (main-product, classe shopify-section-product-section). */
const zoneAchat = () =>
  document.querySelector('[data-svato-main]') || document.querySelector('.shopify-section-product-section');

const define = (name, cls) => {
  if (!customElements.get(name)) customElements.define(name, cls);
};

/* ------------------------------------------------------------------ */
/* Carrousel scroll-snap : flèches, points, barre de progression,      */
/* compteur, clavier. Le swipe est natif (défilement horizontal).     */
/* ------------------------------------------------------------------ */
class SvatoCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    if (!this.track) return;
    this.slides = [...this.track.children];
    this.prev = this.querySelector('[data-carousel-prev]');
    this.next = this.querySelector('[data-carousel-next]');
    this.dots = [...this.querySelectorAll('[data-carousel-dot]')];
    this.progress = this.querySelector('[data-carousel-progress]');
    this.counter = this.querySelector('[data-carousel-counter]');

    this.prev?.addEventListener('click', () => this.step(-1));
    this.next?.addEventListener('click', () => this.step(1));
    // Flèches placées hors du carrousel (ex. en-tête des photos clients) : [data-carousel-for="<id>"][data-dir].
    this.external = this.id ? [...document.querySelectorAll(`[data-carousel-for="${this.id}"]`)] : [];
    this.external.forEach((btn) => btn.addEventListener('click', () => this.step(Number(btn.dataset.dir) || 1)));
    if (this.hasAttribute('data-drag')) this.bindMouseDrag();
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i)));
    this.track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); this.step(1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.step(-1); }
    });
    this.onScroll = () => {
      cancelAnimationFrame(this.raf);
      this.raf = requestAnimationFrame(() => this.update());
    };
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    const start = Number(this.dataset.start || 0);
    if (start > 0 && this.slides.length > start + 1) {
      requestAnimationFrame(() => {
        this.track.style.scrollBehavior = 'auto';
        this.track.scrollLeft = this.offsetFor(this.slides[start]);
        this.track.style.scrollBehavior = '';
        this.update();
      });
    }
    this.update();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onScroll);
  }

  // Glisser à la souris (le doigt utilise le défilement natif) ; un glissement n'ouvre pas la photo.
  bindMouseDrag() {
    let down = false;
    let startX = 0;
    let startLeft = 0;
    let moved = false;
    this.track.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      down = true;
      moved = false;
      startX = e.clientX;
      startLeft = this.track.scrollLeft;
    });
    window.addEventListener('pointermove', (e) => {
      if (!down) return;
      const dx = e.clientX - startX;
      if (!moved && Math.abs(dx) > 5) {
        moved = true;
        this.track.style.scrollSnapType = 'none';
        this.track.style.scrollBehavior = 'auto';
        this.track.classList.add('is-dragging');
      }
      if (moved) this.track.scrollLeft = startLeft - dx;
    }, { passive: true });
    window.addEventListener('pointerup', () => {
      if (!down) return;
      down = false;
      if (!moved) return;
      this.track.classList.remove('is-dragging');
      this.track.style.scrollBehavior = '';
      const i = this.index;
      this.track.style.scrollSnapType = '';
      this.goTo(i);
    });
    this.track.addEventListener('click', (e) => {
      if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; }
    }, true);
    this.track.addEventListener('dragstart', (e) => e.preventDefault());
  }

  get centered() {
    return this.dataset.align === 'center';
  }

  offsetFor(slide) {
    const base = slide.offsetLeft - this.track.offsetLeft;
    return this.centered ? base - (this.track.clientWidth - slide.offsetWidth) / 2 : base;
  }

  get index() {
    const left = this.track.scrollLeft;
    let best = 0;
    let dist = Infinity;
    this.slides.forEach((s, i) => {
      const d = Math.abs(this.offsetFor(s) - left);
      if (d < dist) { dist = d; best = i; }
    });
    return best;
  }

  goTo(i) {
    const slide = this.slides[Math.max(0, Math.min(i, this.slides.length - 1))];
    if (!slide) return;
    this.track.scrollTo({ left: this.offsetFor(slide), behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  step(dir) {
    const visible = Math.max(1, Math.round(this.track.clientWidth / (this.slides[0]?.offsetWidth || 1)));
    const move = this.dataset.stepPage === 'true' ? visible : 1;
    this.goTo(this.index + dir * move);
  }

  update() {
    const max = this.track.scrollWidth - this.track.clientWidth;
    // aria-disabled plutôt que disabled : un bouton qui se désactive sous le focus le ferait perdre.
    this.external?.forEach((btn) => {
      btn.hidden = max <= 2;
      const off = Number(btn.dataset.dir) < 0 ? this.track.scrollLeft <= 2 : this.track.scrollLeft >= max - 2;
      btn.setAttribute('aria-disabled', off ? 'true' : 'false');
    });
    const i = this.index;
    if (this.prev) this.prev.setAttribute('aria-disabled', (this.centered ? i === 0 : this.track.scrollLeft <= 2) ? 'true' : 'false');
    if (this.next) this.next.setAttribute('aria-disabled', (this.centered ? i === this.slides.length - 1 : this.track.scrollLeft >= max - 2) ? 'true' : 'false');
    this.dots.forEach((d, n) => d.setAttribute('aria-current', n === i ? 'true' : 'false'));
    if (this.progress) {
      const ratio = this.track.scrollWidth ? this.track.clientWidth / this.track.scrollWidth : 1;
      const pos = max > 0 ? this.track.scrollLeft / max : 0;
      this.progress.style.setProperty('--svato-progress', `${Math.max(ratio, pos) * 100}%`);
    }
    if (this.counter) this.counter.textContent = `${i + 1} / ${this.slides.length}`;
    this.slides.forEach((s, n) => {
      s.classList.toggle('is-active', n === i);
      // Sans cela, le lecteur d'écran lit toutes les silhouettes alors qu'une seule est visible.
      s.toggleAttribute('inert', n !== i);
      s.setAttribute('aria-hidden', n === i ? 'false' : 'true');
    });
    if (this.hasAttribute('data-autoplay-videos')) {
      this.slides.forEach((s, n) => s.querySelectorAll('video').forEach((v) => {
        if (n === i && !reduceMotion) v.play?.().catch(() => {});
        else v.pause?.();
      }));
    }
    const panels = this.dataset.panels && document.getElementById(this.dataset.panels);
    if (panels) [...panels.children].forEach((p, n) => { p.hidden = n !== i; });
    this.dispatchEvent(new CustomEvent('svato:slide', { detail: { index: i } }));
  }
}
define('svato-carousel', SvatoCarousel);

/* ------------------------------------------------------------------ */
/* Compteur « 1 / N » posé sur la galerie native (Splide).             */
/* ------------------------------------------------------------------ */
class SvatoGalleryCounter extends HTMLElement {
  connectedCallback() {
    const scope = this.closest('[data-svato-main], .shopify-section-product-section') || document;
    this.list = scope.querySelector('[data-ref="main-slider"] .splide__list');
    this.output = this.querySelector('[data-counter-output]');
    if (!this.list || !this.output) return;
    this.observer = new MutationObserver(() => this.update());
    this.observer.observe(this.list, { subtree: true, attributes: true, attributeFilter: ['class'], childList: true });
    this.update();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  update() {
    const slides = [...this.list.querySelectorAll(':scope > .splide__slide:not(.splide__slide--clone)')];
    const visible = slides.filter((s) => s.offsetParent !== null || !s.hidden);
    const active = visible.findIndex((s) => s.classList.contains('is-active'));
    this.output.textContent = `${Math.max(active, 0) + 1} / ${visible.length || slides.length}`;
    this.hidden = (visible.length || slides.length) < 2;
  }
}
define('svato-gallery-counter', SvatoGalleryCounter);

/* ------------------------------------------------------------------ */
/* Ouverture des <dialog> : tout élément [data-svato-open="id"].      */
/* Focus piégé et Échap gérés par showModal(), retour du focus.       */
/* ------------------------------------------------------------------ */
document.addEventListener('click', (e) => {
  const opener = e.target.closest('[data-svato-open]');
  if (opener) {
    const dialog = document.getElementById(opener.dataset.svatoOpen);
    if (dialog?.showModal) {
      e.preventDefault();
      dialog.svatoOpener = opener;
      dialog.showModal();
      opener.setAttribute('aria-expanded', 'true');
      dialog.dispatchEvent(new CustomEvent('svato:open', { bubbles: true, detail: { opener } }));
    }
    return;
  }
  const closer = e.target.closest('[data-svato-close]');
  if (closer) closer.closest('dialog')?.close();
});

document.addEventListener(
  'click',
  (e) => {
    // Clic sur le fond (hors du contenu) = fermeture.
    if (e.target instanceof HTMLDialogElement && e.target.classList.contains('svato-pdp-drawer')) {
      const r = e.target.getBoundingClientRect();
      const inside = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
      if (!inside) e.target.close();
    }
  },
  true,
);

document.addEventListener(
  'close',
  (e) => {
    const dialog = e.target;
    if (dialog instanceof HTMLDialogElement && dialog.svatoOpener) {
      dialog.svatoOpener.setAttribute('aria-expanded', 'false');
      dialog.svatoOpener.focus();
    }
  },
  true,
);

/* ------------------------------------------------------------------ */
/* Onglets ARIA (tissus, gabarits vidéo). Flèches, Début, Fin.         */
/* Les vidéos du panneau actif jouent, les autres sont en pause.      */
/* ------------------------------------------------------------------ */
class SvatoTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')].filter((t) => t.closest('svato-tabs') === this);
    this.tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => this.select(i, true));
      tab.addEventListener('keydown', (e) => {
        const map = { ArrowRight: i + 1, ArrowDown: i + 1, ArrowLeft: i - 1, ArrowUp: i - 1, Home: 0, End: this.tabs.length - 1 };
        if (e.key in map) {
          e.preventDefault();
          const n = (map[e.key] + this.tabs.length) % this.tabs.length;
          this.select(n, true);
          this.tabs[n].focus();
        }
      });
    });
    const start = this.tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
    this.select(start < 0 ? 0 : start, false);
    if (this.hasAttribute('data-autoplay-visible')) this.watchVisibility();
  }

  selectByValue(value) {
    if (!value) return false;
    const v = value.trim().toLowerCase();
    const match = (t) =>
      (t.dataset.value || '').trim().toLowerCase() === v ||
      (t.dataset.contains || '').toLowerCase().split('|').map((x) => x.trim()).includes(v);
    const i = this.tabs.findIndex(match);
    if (i >= 0) this.select(i, false);
    return i >= 0;
  }

  select(index, userAction) {
    this.tabs.forEach((tab, i) => {
      const on = i === index;
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      tab.tabIndex = on ? 0 : -1;
      const panel = document.getElementById(tab.getAttribute('aria-controls'));
      if (!panel) return;
      panel.hidden = !on;
      panel.classList.toggle('is-active', on);
      panel.querySelectorAll('video').forEach((v) => {
        if (on && (this.inView ?? true) && this.hasAttribute('data-autoplay-videos') && !reduceMotion) v.play?.().catch(() => {});
        else v.pause?.();
      });
    });
    this.current = index;
    this.dispatchEvent(new CustomEvent('svato:tab', { detail: { index, userAction } }));
  }

  watchVisibility() {
    const io = new IntersectionObserver(([entry]) => {
      this.inView = entry.isIntersecting;
      this.select(this.current ?? 0, false);
    }, { threshold: 0.25 });
    io.observe(this);
  }
}
define('svato-tabs', SvatoTabs);

/* Bouton lecture / pause d'une vidéo décorative (WCAG 2.2.2). */
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-svato-video-toggle]');
  if (!btn) return;
  const scope = btn.closest('[data-svato-video-scope]') || btn.parentElement;
  const video = [...scope.querySelectorAll('video')].find((v) => !v.closest('[hidden]'));
  if (!video) return;
  if (video.paused) {
    video.play?.().catch(() => {});
    btn.setAttribute('aria-pressed', 'false');
  } else {
    video.pause();
    btn.setAttribute('aria-pressed', 'true');
  }
});

/* ------------------------------------------------------------------ */
/* Points forts : accordéon exclusif + média synchronisé.              */
/* ------------------------------------------------------------------ */
class SvatoFeatures extends HTMLElement {
  connectedCallback() {
    this.items = [...this.querySelectorAll('details[data-feature-index]')];
    this.medias = [...this.querySelectorAll('[data-feature-media]')];
    this.counter = this.querySelector('[data-feature-counter]');
    this.caption = this.querySelector('[data-feature-caption]');
    this.items.forEach((d) => {
      d.addEventListener('toggle', () => {
        if (d.open) {
          this.items.forEach((o) => { if (o !== d) o.open = false; });
          this.show(Number(d.dataset.featureIndex));
        }
      });
    });
    const open = this.items.find((d) => d.open);
    this.show(open ? Number(open.dataset.featureIndex) : 0);
  }

  show(i) {
    this.medias.forEach((m, n) => {
      m.classList.toggle('is-active', n === i);
      m.setAttribute('aria-hidden', n === i ? 'false' : 'true');
      const v = m.querySelector('video');
      if (v) (n === i ? v.play?.().catch(() => {}) : v.pause?.());
    });
    const total = String(this.items.length).padStart(2, '0');
    if (this.counter) this.counter.textContent = `${String(i + 1).padStart(2, '0')} / ${total}`;
    if (this.caption) this.caption.textContent = this.items[i]?.dataset.featureTitle || '';
  }
}
define('svato-features', SvatoFeatures);

/* ------------------------------------------------------------------ */
/* Shop the look : bascule des cartes produit.                         */
/* ------------------------------------------------------------------ */
document.addEventListener('click', (e) => {
  const spot = e.target.closest('[data-svato-hotspot]');
  if (!spot) return;
  const open = spot.getAttribute('aria-expanded') !== 'true';
  spot.setAttribute('aria-expanded', open ? 'true' : 'false');
  const card = document.getElementById(spot.getAttribute('aria-controls'));
  if (card) card.hidden = !open;
});

/* ------------------------------------------------------------------ */
/* Guide des tissus : à l'ouverture, se place sur le tissu choisi.     */
/* ------------------------------------------------------------------ */
document.addEventListener('svato:open', (e) => {
  const dialog = e.target;
  if (!dialog.matches?.('[data-svato-fabric-guide]')) return;
  const optionName = (dialog.dataset.optionName || '').toLowerCase();
  const picker = zoneAchat()?.querySelector('variant-picker');
  let value = '';
  if (picker) {
    const fieldsets = [...picker.querySelectorAll('fieldset')];
    const fs =
      fieldsets.find((f) => (f.querySelector('legend')?.textContent || '').toLowerCase().includes(optionName)) || fieldsets[0];
    value = fs?.querySelector('input:checked')?.value || picker.querySelector('select')?.value || '';
  }
  // Tissu choisi dans le sélecteur ; sinon le premier tissu du canapé (svato.tissus).
  const groups = [...dialog.querySelectorAll('svato-tabs')];
  const tryValue = (v) => groups.map((t) => t.selectByValue?.(v)).some(Boolean);
  if (!tryValue(value)) {
    for (const name of (dialog.dataset.productFabrics || '').split('|').filter(Boolean)) if (tryValue(name)) break;
  }
});

/* ------------------------------------------------------------------ */
/* Barre d'achat sticky : visible quand le bouton principal est sorti  */
/* de l'écran par le haut, masquée près du pied de page.              */
/* ------------------------------------------------------------------ */
class SvatoStickyBar extends HTMLElement {
  connectedCallback() {
    this.main = zoneAchat();
    this.mainButton = this.main?.querySelector('[data-ref="add-to-cart-button"]');
    this.button = this.querySelector('[data-sticky-add]');
    this.variantLabel = this.querySelector('[data-sticky-variant]');
    this.price = this.querySelector('[data-sticky-price]');
    this.compare = this.querySelector('[data-sticky-compare]');
    this.image = this.querySelector('.svato-pdp-sticky__image');
    if (!this.mainButton) return;

    this.button?.addEventListener('click', () => {
      const btn = this.main.querySelector('[data-ref="add-to-cart-button"]');
      if (btn && !btn.disabled) btn.click();
      else this.main.querySelector('variant-picker')?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
    });
    this.querySelector('[data-sticky-change]')?.addEventListener('click', () => {
      const picker = this.main.querySelector('variant-picker');
      picker?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
      picker?.querySelector('input:checked, select')?.focus({ preventScroll: true });
    });

    this.buttonAbove = false;
    this.footerVisible = false;
    this.io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target === this.mainButtonWrap) {
          this.buttonAbove = !entry.isIntersecting;
        } else {
          this.footerVisible = entry.isIntersecting;
        }
      });
      this.toggle(this.buttonAbove && !this.footerVisible);
    });
    this.mainButtonWrap = this.mainButton.closest('[data-ref="add-to-cart-button-container"]') || this.mainButton;
    this.io.observe(this.mainButtonWrap);
    const end = document.querySelector('[data-svato-sticky-end]') || document.querySelector('footer, .shopify-section-group-footer-group');
    if (end) this.io.observe(end);

    document.addEventListener('variant:updated', (e) => this.onVariant(e));
  }

  disconnectedCallback() {
    this.io?.disconnect();
  }

  toggle(show) {
    this.dataset.visible = show ? 'true' : 'false';
    this.toggleAttribute('inert', !show);
    this.setAttribute('aria-hidden', show ? 'false' : 'true');
  }

  onVariant(e) {
    const v = e.detail?.resource;
    if (!v) return;
    if (this.variantLabel && v.public_title !== undefined) this.variantLabel.textContent = v.public_title || '';
    if (this.price && typeof v.price === 'number') {
      const money = new Intl.NumberFormat(document.documentElement.lang || 'fr', {
        style: 'currency',
        currency: window.Theme?.currency || 'EUR',
      }).format(v.price / 100);
      this.price.textContent = money;
    }
    // Prix barré : affiché seulement si la variante a un prix avant réduction supérieur au prix.
    if (this.compare && typeof v.price === 'number') {
      const barre = typeof v.compare_at_price === 'number' && v.compare_at_price > v.price;
      this.compare.textContent = barre
        ? new Intl.NumberFormat(document.documentElement.lang || 'fr', { style: 'currency', currency: window.Theme?.currency || 'EUR' }).format(v.compare_at_price / 100)
        : '';
    }
    if (this.button) this.button.disabled = v.available === false;
    const src = v.featured_media?.preview_image?.src || v.featured_image?.src;
    if (this.image && src) {
      const url = new URL(src, location.origin);
      url.searchParams.set('width', '120');
      this.image.src = url.toString();
      this.image.removeAttribute('srcset');
    }
  }
}
define('svato-sticky-bar', SvatoStickyBar);

/* ------------------------------------------------------------------ */
/* Navigation interne : masque les liens sans cible, suit la section   */
/* visible (aria-current).                                             */
/* ------------------------------------------------------------------ */
class SvatoAnchors extends HTMLElement {
  connectedCallback() {
    this.links = [...this.querySelectorAll('a[href^="#"]')];
    this.targets = new Map();
    const design = this.hasAttribute('data-design-mode');
    this.links.forEach((a) => {
      const id = decodeURIComponent(a.getAttribute('href').slice(1));
      const target = id && document.getElementById(id);
      if (target) this.targets.set(target, a);
      else if (design) a.setAttribute('data-missing', '');
      else (a.closest('li') || a).setAttribute('hidden', '');
    });
    // Barre Mopio : hauteur de l'en-tête collant (s'il l'est) + décalage réglé, et défilement
    // qui s'arrête sous la barre.
    this.wrapper = this.closest('.shopify-section');
    this.measure = () => {
      const header = document.querySelector('.section-header');
      const pinned = header && getComputedStyle(header).position === 'sticky' ? header.offsetHeight : 0;
      const offset = parseFloat(getComputedStyle(this).getPropertyValue('--anchors-offset')) || 0;
      this.top = pinned + offset;
      this.wrapper?.style.setProperty('--svato-anchors-top', `${this.top}px`);
    };
    this.measure();
    window.addEventListener('resize', this.measure, { passive: true });
    // L'en-tête change de hauteur au défilement : on suit sa taille réelle.
    const headerEl = document.querySelector('.section-header');
    if (headerEl && 'ResizeObserver' in window) {
      this.ro = new ResizeObserver(this.measure);
      this.ro.observe(headerEl);
    }
    this.links.forEach((a) => a.addEventListener('click', (e) => {
      const target = [...this.targets].find(([, link]) => link === a)?.[0];
      if (!target) return;
      e.preventDefault();
      const y = target.getBoundingClientRect().top + scrollY - this.top - this.offsetHeight;
      window.scrollTo({ top: Math.max(0, y), behavior: reduceMotion ? 'auto' : 'smooth' });
      this.links.forEach((l) => l.removeAttribute('aria-current'));
      a.setAttribute('aria-current', 'true');
    }));
    if (this.targets.size === 0) {
      if (!design) (this.wrapper || this).setAttribute('hidden', '');
      return;
    }
    this.io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          this.links.forEach((l) => l.removeAttribute('aria-current'));
          const link = this.targets.get(entry.target);
          link?.setAttribute('aria-current', 'true');
          const bar = this.querySelector('[data-anchors-track]');
          if (link && bar) bar.scrollLeft = link.offsetLeft - (bar.clientWidth - link.offsetWidth) / 2;
        });
      },
      { rootMargin: '-40% 0px -55% 0px' },
    );
    this.targets.forEach((_, t) => this.io.observe(t));
  }

  disconnectedCallback() {
    this.io?.disconnect();
    window.removeEventListener('resize', this.measure);
    this.ro?.disconnect();
  }
}
define('svato-anchors', SvatoAnchors);

/* ------------------------------------------------------------------ */
/* Visionneuse (photos clients) : s'ouvre sur la photo cliquée.        */
/* ------------------------------------------------------------------ */
document.addEventListener('svato:open', (e) => {
  const dialog = e.target;
  if (!dialog.matches?.('[data-svato-lightbox]')) return;
  const carousel = dialog.querySelector('svato-carousel');
  const proxy = dialog.querySelector('[data-carousel-counter-proxy]');
  const index = Number(e.detail?.opener?.dataset.svatoIndex || 0);
  if (!carousel) return;
  if (!carousel.dataset.bound) {
    carousel.dataset.bound = 'true';
    carousel.addEventListener('svato:slide', (ev) => {
      if (proxy) proxy.textContent = `${ev.detail.index + 1} / ${carousel.slides.length}`;
    });
  }
  requestAnimationFrame(() => {
    const slide = carousel.slides?.[index];
    if (slide) carousel.track.scrollLeft = slide.offsetLeft - carousel.track.offsetLeft;
    carousel.update?.();
    carousel.track.focus({ preventScroll: true });
  });
});

/* ------------------------------------------------------------------ */
/* Galerie de la fiche (bloc _svato-pdp-gallery) : carrousel carré,    */
/* vignettes mobiles, pastille vidéo, visionneuse plein écran,         */
/* synchronisation avec la variante choisie.                          */
/* ------------------------------------------------------------------ */
/* Calage de sécurité : au repos (doigt levé, défilement fini), une piste à défilement
   horizontal rejoint le média le plus proche. Complète scroll-snap, qui ne cale pas
   toujours après un glissement tactile lent. */
function calerAuRepos(track, surIndex) {
  if (!track || track.dataset.calage) return;
  track.dataset.calage = 'true';
  let minuteur;
  let doigtPose = false;
  const planifier = () => {
    clearTimeout(minuteur);
    minuteur = setTimeout(() => {
      // Tant que le doigt est posé, c'est le navigateur qui mène : on ne recale pas.
      // (On ne peut pas se fier à pointercancel : il est émis dès qu'un défilement démarre.)
      if (doigtPose) return;
      const largeur = Math.max(1, track.clientWidth);
      const i = Math.round(track.scrollLeft / largeur);
      if (Math.abs(track.scrollLeft - i * largeur) > 2) {
        track.scrollTo({ left: i * largeur, behavior: reduceMotion ? 'auto' : 'smooth' });
      }
      surIndex?.(i);
    }, 160);
  };
  track.addEventListener('touchstart', () => { doigtPose = true; clearTimeout(minuteur); }, { passive: true });
  track.addEventListener('touchmove', () => { clearTimeout(minuteur); }, { passive: true });
  for (const ev of ['touchend', 'touchcancel']) {
    track.addEventListener(ev, () => { doigtPose = false; planifier(); }, { passive: true });
  }
  track.addEventListener('scroll', planifier, { passive: true });
}

class SvatoGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    if (!this.track) return;
    this.slides = [...this.track.children];
    this.thumbs = [...this.querySelectorAll('[data-thumb]')];
    this.dots = [...this.querySelectorAll('[data-dot]')];
    this.index = 0;
    this.dots.forEach((d) => d.addEventListener('click', () => this.go(Number(d.dataset.dot))));
    this.querySelector('[data-zoom]')?.addEventListener('click', () => this.openLightbox(this.index));

    this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1));
    this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1));
    this.thumbs.forEach((t) => t.addEventListener('click', () => this.go(Number(t.dataset.thumb))));
    this.querySelector('[data-video-index]')?.addEventListener('click', (e) => {
      const i = Number(e.currentTarget.dataset.videoIndex);
      this.go(i);
      this.slides[i]?.querySelector('video')?.play().catch(() => {});
    });

    let ticking = false;
    this.track.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        const i = Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth));
        if (i !== this.index) this.setActive(i);
      });
    }, { passive: true });
    calerAuRepos(this.track, (i) => this.setActive(i));
    this.track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.index - 1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.index + 1); }
    });

    this.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-open-lightbox]');
      if (opener && this.contains(opener)) this.openLightbox(Number(opener.dataset.openLightbox));
    });
    this.setupLightbox();

    this.onVariant = (e) => {
      const id = e.detail?.resource?.featured_media?.id;
      if (!id) return;
      const i = this.slides.findIndex((s) => s.dataset.mediaId === String(id));
      if (i >= 0) this.go(i);
    };
    document.addEventListener('variant:updated', this.onVariant);
    this.setActive(0);
  }

  disconnectedCallback() {
    document.removeEventListener('variant:updated', this.onVariant);
  }

  go(i) {
    const n = this.slides.length;
    const target = (i + n) % n;
    this.track.scrollTo({ left: target * this.track.clientWidth, behavior: reduceMotion ? 'auto' : 'smooth' });
    this.setActive(target);
  }

  setActive(i) {
    if (this.slides[this.index] && this.index !== i) {
      this.slides[this.index].querySelector('video')?.pause();
    }
    this.index = i;
    this.dots?.forEach((d, k) => (k === i ? d.setAttribute('aria-current', 'true') : d.removeAttribute('aria-current')));
    this.thumbs.forEach((t, k) => {
      if (k === i) {
        t.setAttribute('aria-current', 'true');
        const strip = t.parentElement;
        const vertical = strip.scrollHeight > strip.clientHeight + 2;
        if (vertical) {
          const haut = t.offsetTop - strip.offsetTop;
          if (haut < strip.scrollTop || haut + t.offsetHeight > strip.scrollTop + strip.clientHeight) {
            strip.scrollTo({ top: haut - 12, behavior: reduceMotion ? 'auto' : 'smooth' });
          }
        } else {
          const left = t.offsetLeft - strip.offsetLeft;
          if (left < strip.scrollLeft || left + t.offsetWidth > strip.scrollLeft + strip.clientWidth) {
            strip.scrollTo({ left: left - 18, behavior: reduceMotion ? 'auto' : 'smooth' });
          }
        }
      } else t.removeAttribute('aria-current');
    });
  }

  setupLightbox() {
    this.dialog = this.querySelector('[data-lightbox-dialog]');
    if (!this.dialog) return;
    this.lbTrack = this.dialog.querySelector('[data-lb-track]');
    this.lbSlides = [...this.lbTrack.children];
    this.lbCurrent = this.dialog.querySelector('[data-lb-current]');
    this.lbDots = [...this.dialog.querySelectorAll('[data-lb-dot]')];
    this.lbDots.forEach((d) => d.addEventListener('click', () => this.lbGo(Number(d.dataset.lbDot))));
    this.lbIndex = 0;
    this.dialog.querySelector('[data-lb-close]')?.addEventListener('click', () => this.dialog.close());
    this.dialog.querySelector('[data-lb-prev]')?.addEventListener('click', () => this.lbGo(this.lbIndex - 1));
    this.dialog.querySelector('[data-lb-next]')?.addEventListener('click', () => this.lbGo(this.lbIndex + 1));
    this.dialog.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') this.lbGo(this.lbIndex - 1);
      if (e.key === 'ArrowRight') this.lbGo(this.lbIndex + 1);
    });
    this.dialog.addEventListener('click', (e) => {
      if (e.target === this.dialog || e.target === this.lbTrack || e.target.classList.contains('svato-lightbox__slide')) this.dialog.close();
    });
    this.dialog.addEventListener('close', () => {
      this.lbSlides.forEach((s) => s.querySelector('video')?.pause());
      document.documentElement.style.removeProperty('overflow');
    });
    calerAuRepos(this.lbTrack, (i) => this.lbSetActive(i));
    let ticking = false;
    this.lbTrack.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        const i = Math.round(this.lbTrack.scrollLeft / Math.max(1, this.lbTrack.clientWidth));
        if (i !== this.lbIndex) this.lbSetActive(i);
      });
    }, { passive: true });
  }

  openLightbox(i) {
    if (!this.dialog) return;
    this.dialog.showModal();
    document.documentElement.style.overflow = 'hidden';
    this.lbTrack.scrollLeft = i * this.lbTrack.clientWidth;
    this.lbSetActive(i);
  }

  lbGo(i) {
    const n = this.lbSlides.length;
    const target = (i + n) % n;
    this.lbTrack.scrollTo({ left: target * this.lbTrack.clientWidth, behavior: reduceMotion ? 'auto' : 'smooth' });
    this.lbSetActive(target);
  }

  lbSetActive(i) {
    if (this.lbSlides[this.lbIndex] && this.lbIndex !== i) this.lbSlides[this.lbIndex].querySelector('video')?.pause();
    this.lbIndex = i;
    // Charge l'image affichée et ses voisines : le chargement différé ne part pas seul dans la visionneuse.
    const n = this.lbSlides.length;
    [i - 1, i, i + 1].forEach((k) => {
      const img = this.lbSlides[(k + n) % n]?.querySelector('img[loading="lazy"]');
      if (img) img.loading = 'eager';
    });
    if (this.lbCurrent) this.lbCurrent.textContent = String(i + 1);
    this.lbDots?.forEach((d, k) => (k === i ? d.setAttribute('aria-current', 'true') : d.removeAttribute('aria-current')));
  }
}
define('svato-gallery', SvatoGallery);

/* ------------------------------------------------------------------ */
/* Notre visionneuse, branchée sur la galerie native FullStack          */
/* (21/09/2026). Hugo garde la galerie native (format, grille, etc.)    */
/* mais veut NOTRE visionneuse quand on agrandit : même disposition,     */
/* mêmes boutons, même croix. Le balisage est le snippet svato-lightbox, */
/* partagé avec notre galerie ; SvatoLightbox en reprend la logique      */
/* (SvatoGallery.setupLightbox / openLightbox / lbGo / lbSetActive).     */
/* ------------------------------------------------------------------ */
class SvatoLightbox {
  constructor(dialog) {
    this.dialog = dialog;
    this.track = dialog.querySelector('[data-lb-track]');
    this.slides = [...this.track.children];
    this.current = dialog.querySelector('[data-lb-current]');
    this.dots = [...dialog.querySelectorAll('[data-lb-dot]')];
    this.index = 0;
    this.dots.forEach((d) => d.addEventListener('click', () => this.go(Number(d.dataset.lbDot))));
    dialog.querySelector('[data-lb-close]')?.addEventListener('click', () => dialog.close());
    dialog.querySelector('[data-lb-prev]')?.addEventListener('click', () => this.go(this.index - 1));
    dialog.querySelector('[data-lb-next]')?.addEventListener('click', () => this.go(this.index + 1));
    dialog.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') this.go(this.index - 1);
      if (e.key === 'ArrowRight') this.go(this.index + 1);
    });
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog || e.target === this.track || e.target.classList.contains('svato-lightbox__slide')) dialog.close();
    });
    dialog.addEventListener('close', () => {
      this.slides.forEach((sl) => sl.querySelector('video')?.pause());
      document.documentElement.style.removeProperty('overflow');
      this.retour?.focus({ preventScroll: true });
    });
    calerAuRepos(this.track, (i) => this.setActive(i));
    let ticking = false;
    this.track.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        const i = Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth));
        if (i !== this.index) this.setActive(i);
      });
    }, { passive: true });
  }

  open(i, retour) {
    this.retour = retour || null;
    this.dialog.showModal();
    document.documentElement.style.overflow = 'hidden';
    this.track.scrollLeft = i * this.track.clientWidth;
    this.setActive(i);
  }

  go(i) {
    const n = this.slides.length;
    const target = (i + n) % n;
    this.track.scrollTo({ left: target * this.track.clientWidth, behavior: reduceMotion ? 'auto' : 'smooth' });
    this.setActive(target);
  }

  setActive(i) {
    if (this.slides[this.index] && this.index !== i) this.slides[this.index].querySelector('video')?.pause();
    this.index = i;
    const n = this.slides.length;
    [i - 1, i, i + 1].forEach((k) => {
      const img = this.slides[(k + n) % n]?.querySelector('img[loading="lazy"]');
      if (img) img.loading = 'eager';
    });
    if (this.current) this.current.textContent = String(i + 1);
    this.dots.forEach((d, k) => (k === i ? d.setAttribute('aria-current', 'true') : d.removeAttribute('aria-current')));
  }
}

class SvatoZoomNatif extends HTMLElement {
  connectedCallback() {
    if (this.lb) return;
    const dialog = this.querySelector('[data-lightbox-dialog]');
    if (!dialog) return;
    this.lb = new SvatoLightbox(dialog);
    this.portee = this.closest('.svato-pdp-main, .shopify-section') || document;

    // Un appui ouvre la visionneuse ; un glissé du carrousel, non. On mesure donc le geste.
    this.depart = null;
    this.onDown = (e) => {
      if (!e.target.closest?.('product-media-gallery [data-zoom-trigger]')) return;
      this.depart = { x: e.clientX, y: e.clientY };
    };
    // Phase de capture au niveau du document : on passe avant l'écouteur de la galerie
    // native (product-zoom.js, en remontée sur product-media-gallery) et on l'arrête.
    this.onClick = (e) => {
      const trigger = e.target.closest?.('[data-zoom-trigger]');
      if (!trigger || !this.portee.contains(trigger) || !trigger.closest('product-media-gallery')) return;
      e.preventDefault();
      e.stopPropagation();
      const d = this.depart;
      this.depart = null;
      if (d && Math.hypot(e.clientX - d.x, e.clientY - d.y) > 10) return; // c'était un glissé
      const id = trigger.getAttribute('data-zoom-media-id');
      const i = this.lb.slides.findIndex((sl) => sl.dataset.mediaId === id);
      this.lb.open(Math.max(0, i), trigger);
    };
    document.addEventListener('pointerdown', this.onDown, true);
    document.addEventListener('click', this.onClick, true);
  }

  disconnectedCallback() {
    document.removeEventListener('pointerdown', this.onDown, true);
    document.removeEventListener('click', this.onClick, true);
    this.lb = null;
  }
}
define('svato-zoom-natif', SvatoZoomNatif);

/* ------------------------------------------------------------------ */
/* Produits complémentaires (bloc _svato-pdp-upsell) : pages, quantité, */
/* et ajout au panier avec le canapé via le crochet toggleCrossSells   */
/* de product-form (même requête /cart/add que le produit principal).  */
/* ------------------------------------------------------------------ */
class SvatoUpsell extends HTMLElement {
  connectedCallback() {
    this.cards = [...this.querySelectorAll('.svato-pdp-upsell__card')];
    this.pages = Math.max(1, ...this.cards.map((c) => Number(c.dataset.page) + 1));
    this.page = 0;
    this.querySelector('[data-upsell-prev]')?.addEventListener('click', () => this.show(this.page - 1));
    this.querySelector('[data-upsell-next]')?.addEventListener('click', () => this.show(this.page + 1));
    this.cards.forEach((card) => {
      const input = card.querySelector('[data-upsell-qty]');
      const minus = card.querySelector('[data-upsell-minus]');
      const sync = () => minus?.setAttribute('aria-disabled', Number(input.value || 1) <= 1 ? 'true' : 'false');
      minus?.addEventListener('click', () => { input.value = Math.max(1, Number(input.value || 1) - 1); sync(); });
      card.querySelector('[data-upsell-plus]')?.addEventListener('click', () => { input.value = Number(input.value || 1) + 1; sync(); });
      input?.addEventListener('change', sync);
      sync();
    });
    // « Vous aimerez aussi » : simples liens, rien n'est ajouté au panier.
    if (this.hasAttribute('data-links-only')) return;
    customElements.whenDefined('product-form').then(() => requestAnimationFrame(() => {
      const form = this.closest('.shopify-section')?.querySelector('product-form');
      if (form && !form.toggleCrossSells) form.toggleCrossSells = this;
    }));
  }

  show(p) {
    this.page = (p + this.pages) % this.pages;
    this.cards.forEach((c) => { c.hidden = Number(c.dataset.page) !== this.page; });
  }

  getSelectedItems() {
    const items = this.cards
      .map((c) => ({ box: c.querySelector('.svato-pdp-upsell__check'), qty: c.querySelector('[data-upsell-qty]') }))
      .filter(({ box }) => box && box.checked && !box.disabled && box.value)
      .map(({ box, qty }) => ({ id: box.value, quantity: Math.max(1, Number(qty?.value || 1)) }));
    return { items, total_price: 0, total_compare_at_price: 0 };
  }
}
define('svato-upsell', SvatoUpsell);

/* Montant de l'échéance (bloc _svato-pdp-installments) recalculé au changement de variante. */
document.addEventListener('variant:updated', (e) => {
  const v = e.detail?.resource;
  if (!v || typeof v.price !== 'number') return;
  document.querySelectorAll('.svato-pdp-installments').forEach((box) => {
    const out = box.querySelector('[data-installment-amount]');
    const n = Number(box.dataset.installments || 3);
    if (!out) return;
    out.textContent = new Intl.NumberFormat(document.documentElement.lang || 'fr', {
      style: 'currency',
      currency: window.Theme?.currency || 'EUR',
    }).format(Math.floor(v.price / n) / 100);
  });
});

/* ------------------------------------------------------------------ */
/* « Pourquoi l'aimer » (section svato-pdp-why) : carrousel centré en  */
/* boucle infinie à la manière du Swiper de Mopio. Les diapositives    */
/* sont clonées avant et après ; le texte et les points suivent la     */
/* diapositive active ; défilement auto, glisser au doigt, clavier.   */
/* ------------------------------------------------------------------ */
class SvatoWhy extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-why-viewport]');
    this.track = this.querySelector('[data-why-track]');
    if (!this.track || this.track.dataset.ready) return;
    this.track.dataset.ready = 'true';
    this.originals = [...this.track.children];
    this.n = this.originals.length;
    this.panels = [...this.querySelectorAll('[data-why-panel]')];
    this.dots = [...this.querySelectorAll('[data-why-dot]')];
    this.paused = new Set();
    if (this.n === 0) return;
    this.loop = this.n > 1;
    if (this.loop) {
      const clone = (s) => {
        const c = s.cloneNode(true);
        c.setAttribute('aria-hidden', 'true');
        c.inert = true;
        c.dataset.clone = '';
        c.removeAttribute('data-shopify-editor-block');
        return c;
      };
      this.track.prepend(...this.originals.map(clone));
      this.track.append(...this.originals.map(clone));
    }
    this.slides = [...this.track.children];
    const start = Math.min(Math.max(Number(this.dataset.start || 0), 0), this.n - 1);
    this.pos = (this.loop ? this.n : 0) + start;

    this.querySelector('[data-why-prev]')?.addEventListener('click', () => this.go(this.pos - 1));
    this.querySelector('[data-why-next]')?.addEventListener('click', () => this.go(this.pos + 1));
    this.dots.forEach((d, i) => d.addEventListener('click', () => this.go(this.pos + this.shortest(i))));
    this.slides.forEach((s, i) => s.addEventListener('click', (e) => {
      if (this.moved || i === this.pos) return;
      e.preventDefault();
      this.go(i);
    }));
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.pos + 1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.pos - 1); }
    });
    this.track.addEventListener('transitionend', (e) => {
      if (e.target === this.track && e.propertyName === 'transform') this.normalize();
    });
    this.bindDrag();

    // Commande pause / lecture : le défilement automatique doit pouvoir être arrêté.
    this.toggle = this.querySelector('[data-why-toggle]');
    this.toggle?.addEventListener('click', () => {
      const enPause = this.paused.has('manuel');
      if (enPause) this.paused.delete('manuel'); else this.paused.add('manuel');
      this.toggle.dataset.state = enPause ? 'lecture' : 'pause';
      this.toggle.setAttribute('aria-label', enPause ? this.toggle.dataset.labelPause : this.toggle.dataset.labelPlay);
      this.schedule();
    });
    this.onResize = () => this.render(false);
    window.addEventListener('resize', this.onResize, { passive: true });

    // Éditeur : sélectionner un bloc « Argument » amène sa diapositive au centre.
    this.addEventListener('shopify:block:select', (e) => {
      const i = this.originals.indexOf(e.target.closest('[data-why-slide]'));
      if (i < 0) return;
      this.paused.add('editor');
      this.go(this.pos + this.shortest(i));
    });
    this.addEventListener('shopify:block:deselect', () => { this.paused.delete('editor'); this.schedule(); });

    // Défilement automatique : suspendu au survol, au focus, hors écran et onglet caché.
    this.delay = Number(this.dataset.delay || 0);
    if (this.delay > 0 && this.loop && !reduceMotion) {
      const hold = (why, on) => { on ? this.paused.add(why) : this.paused.delete(why); this.schedule(); };
      this.addEventListener('pointerenter', (e) => e.pointerType === 'mouse' && hold('hover', true));
      this.addEventListener('pointerleave', () => hold('hover', false));
      this.addEventListener('focusin', () => hold('focus', true));
      this.addEventListener('focusout', () => hold('focus', false));
      this.io = new IntersectionObserver(([en]) => hold('offscreen', !en.isIntersecting));
      this.io.observe(this);
      this.onVisibility = () => hold('hidden', document.hidden);
      document.addEventListener('visibilitychange', this.onVisibility);
    }
    requestAnimationFrame(() => this.render(false));
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.io?.disconnect();
    clearTimeout(this.timer);
  }

  get real() {
    return ((this.pos % this.n) + this.n) % this.n;
  }

  // Écart le plus court (en diapositives) entre l'index réel actif et l'index réel i.
  shortest(i) {
    let d = i - this.real;
    if (this.loop) {
      if (d > this.n / 2) d -= this.n;
      if (d < -this.n / 2) d += this.n;
    }
    return d;
  }

  schedule() {
    clearTimeout(this.timer);
    if (this.delay > 0 && this.loop && !reduceMotion && this.paused.size === 0) {
      this.timer = setTimeout(() => this.go(this.pos + 1), this.delay);
    }
  }

  go(pos) {
    if (!this.loop) pos = Math.max(0, Math.min(pos, this.n - 1));
    // Hors des bornes des clones (clics rapides) : on se recale d'abord sans animation.
    if (this.loop && (pos < 1 || pos > this.slides.length - 2)) {
      const delta = pos - this.pos;
      this.pos = this.n + this.real;
      this.render(false);
      pos = this.pos + delta;
    }
    this.pos = pos;
    this.render(true);
  }

  // Revient sur la copie « originale » équivalente, sans animation (boucle invisible).
  normalize() {
    if (!this.loop) return;
    if (this.pos < this.n || this.pos >= 2 * this.n) {
      this.pos = this.n + this.real;
      this.render(false);
    }
  }

  offset(pos, extra = 0) {
    const slide = this.slides[pos];
    return slide.offsetLeft - (this.viewport.clientWidth - slide.offsetWidth) / 2 - extra;
  }

  render(animate) {
    const slide = this.slides[this.pos];
    if (!slide) return;
    const instant = !animate || reduceMotion;
    this.classList.toggle('is-instant', instant);
    this.track.style.transition = instant ? 'none' : '';
    this.track.style.transform = `translate3d(${-this.offset(this.pos)}px, 0, 0)`;
    this.slides.forEach((s, i) => s.classList.toggle('is-active', i === this.pos));
    if (instant) void this.track.offsetWidth;
    const real = this.real;
    this.panels.forEach((p, i) => { p.hidden = i !== real; });
    this.dots.forEach((d, i) => d.setAttribute('aria-current', i === real ? 'true' : 'false'));
    this.slides.forEach((s, i) => s.querySelectorAll('video').forEach((v) => {
      if (i === this.pos && !reduceMotion) v.play?.().catch(() => {});
      else v.pause?.();
    }));
    if (instant) requestAnimationFrame(() => requestAnimationFrame(() => this.classList.remove('is-instant')));
    this.schedule();
  }

  bindDrag() {
    let startX = 0;
    let startY = 0;
    let dx = 0;
    let dragging = false;
    this.viewport.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      dragging = true;
      this.moved = false;
      startX = e.clientX;
      startY = e.clientY;
      dx = 0;
      this.paused.add('drag');
      clearTimeout(this.timer);
    });
    window.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      dx = e.clientX - startX;
      const dy = e.clientY - startY;
      if (!this.moved && Math.abs(dy) > 8 && Math.abs(dy) > Math.abs(dx)) {
        dragging = false;
        this.paused.delete('drag');
        this.schedule();
        return;
      }
      if (Math.abs(dx) > 6) this.moved = true;
      if (!this.moved) return;
      this.track.style.transition = 'none';
      this.track.style.transform = `translate3d(${-this.offset(this.pos, dx)}px, 0, 0)`;
    }, { passive: true });
    const end = () => {
      if (!dragging) return;
      dragging = false;
      this.paused.delete('drag');
      const width = this.slides[this.pos]?.offsetWidth || 1;
      const steps = Math.abs(dx) > 40 ? -Math.sign(dx) * Math.max(1, Math.round(Math.abs(dx) / width)) : 0;
      this.go(this.pos + steps);
      setTimeout(() => { this.moved = false; }, 0);
    };
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
    this.viewport.addEventListener('dragstart', (e) => e.preventDefault());
  }
}
define('svato-why', SvatoWhy);

/* ------------------------------------------------------------------ */
/* « À votre mesure » (section svato-pdp-fits) : diapositives en       */
/* fondu pilotées par des pastilles, changement automatique, bouton    */
/* lecture / pause ; la vidéo active repart de zéro (comme Mopio).     */
/* ------------------------------------------------------------------ */
class SvatoFits extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-fits-slide]')];
    this.tabs = [...this.querySelectorAll('[data-fits-tab]')];
    this.toggle = this.querySelector('[data-fits-toggle]');
    if (this.slides.length === 0) return;
    this.index = 0;
    // État initial : une seule silhouette exposée au lecteur d'écran.
    this.slides.forEach((slide, n) => { slide.toggleAttribute('inert', n !== 0); slide.setAttribute('aria-hidden', n === 0 ? 'false' : 'true'); });
    this.delay = Number(this.dataset.delay || 0);
    this.running = this.delay > 0 && this.slides.length > 1 && !reduceMotion;
    this.tabs.forEach((t, i) => t.addEventListener('click', () => { this.show(i); this.restart(); }));
    this.tabs.forEach((t, i) => t.addEventListener('keydown', (e) => {
      const n = this.tabs.length;
      const next = e.key === 'ArrowRight' ? (i + 1) % n : e.key === 'ArrowLeft' ? (i - 1 + n) % n : null;
      if (next === null) return;
      e.preventDefault();
      this.tabs[next].focus();
      this.show(next);
      this.restart();
    }));
    this.toggle?.addEventListener('click', () => {
      this.running = !this.running;
      this.paused = !this.running;
      if (this.running) { this.play(); this.restart(); } else { clearTimeout(this.timer); this.pauseAll(); }
      this.syncToggle();
    });
    this.addEventListener('shopify:block:select', (e) => {
      const i = this.slides.indexOf(e.target.closest('[data-fits-slide]'));
      if (i >= 0) { clearTimeout(this.timer); this.show(i); }
    });
    // Hors écran : on coupe vidéo et minuterie.
    this.io = new IntersectionObserver(([en]) => {
      this.visible = en.isIntersecting;
      if (this.visible && !this.paused) { this.play(); this.restart(); } else { clearTimeout(this.timer); this.pauseAll(); }
    });
    this.io.observe(this);
    if (reduceMotion) this.paused = true;
    this.syncToggle();
  }

  disconnectedCallback() {
    this.io?.disconnect();
    clearTimeout(this.timer);
  }

  show(i) {
    this.index = i;
    this.slides.forEach((s, n) => {
      s.classList.toggle('is-active', n === i);
      // Sans cela, le lecteur d'écran lit toutes les silhouettes alors qu'une seule est visible.
      s.toggleAttribute('inert', n !== i);
      s.setAttribute('aria-hidden', n === i ? 'false' : 'true');
    });
    this.tabs.forEach((t, n) => {
      t.setAttribute('aria-selected', n === i ? 'true' : 'false');
      t.tabIndex = n === i ? 0 : -1;
    });
    if (!this.paused) this.play();
  }

  pauseAll() {
    this.slides.forEach((s) => s.querySelectorAll('video').forEach((v) => v.pause()));
  }

  play() {
    this.pauseAll();
    const v = this.slides[this.index]?.querySelector('video');
    if (!v) return;
    try { v.currentTime = 0; } catch (e) { /* vidéo pas encore chargée */ }
    v.play?.().catch(() => {});
  }

  restart() {
    clearTimeout(this.timer);
    if (!this.running || this.paused || this.visible === false) return;
    this.timer = setTimeout(() => {
      this.show((this.index + 1) % this.slides.length);
      this.restart();
    }, this.delay);
  }

  syncToggle() {
    if (!this.toggle) return;
    const paused = !!this.paused;
    this.toggle.classList.toggle('is-paused', paused);
    this.toggle.setAttribute('aria-label', paused ? this.toggle.dataset.labelPlay : this.toggle.dataset.labelPause);
  }
}
define('svato-fits', SvatoFits);

/* ------------------------------------------------------------------ */
/* FAQ (section svato-pdp-faq) : ouverture / fermeture animée,         */
/* option « une seule question ouverte », clavier haut / bas.          */
/* ------------------------------------------------------------------ */
class SvatoFaq extends HTMLElement {
  connectedCallback() {
    this.triggers = [...this.querySelectorAll('[data-faq-trigger]')];
    this.triggers.forEach((btn, i) => {
      this.sync(btn);
      btn.addEventListener('click', () => {
        const willOpen = btn.getAttribute('aria-expanded') !== 'true';
        if (willOpen && this.hasAttribute('data-single')) {
          this.triggers.forEach((other) => { if (other !== btn) this.set(other, false); });
        }
        this.set(btn, willOpen);
      });
      btn.addEventListener('keydown', (e) => {
        const n = this.triggers.length;
        const map = { ArrowDown: (i + 1) % n, ArrowUp: (i - 1 + n) % n, Home: 0, End: n - 1 };
        if (!(e.key in map)) return;
        e.preventDefault();
        this.triggers[map[e.key]].focus();
      });
    });
    this.addEventListener('shopify:block:select', (e) => {
      const btn = e.target.querySelector?.('[data-faq-trigger]');
      if (btn) this.set(btn, true);
    });
  }

  panel(btn) {
    return document.getElementById(btn.getAttribute('aria-controls'));
  }

  // Panneau fermé : masqué (hidden) et hors du parcours clavier / lecteur d'écran (inert).
  sync(btn) {
    const open = btn.getAttribute('aria-expanded') === 'true';
    const panel = this.panel(btn);
    if (!panel) return;
    panel.hidden = !open;
    panel.inert = !open;
  }

  set(btn, open) {
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    this.sync(btn);
  }
}
define('svato-faq', SvatoFaq);

/* ------------------------------------------------------------------ */
/* « Vus récemment » (section svato-pdp-recent), d'après Koala :       */
/* mémorise les fiches consultées (localStorage), charge leurs cartes  */
/* via /products/<handle>?view=svato-card, complète avec la collection */
/* de repli ; flèches, barre de progression, pastilles de couleur.    */
/* ------------------------------------------------------------------ */
const SVATO_SEEN_KEY = 'svato:vus';
const svatoSeen = {
  read() {
    try { return JSON.parse(localStorage.getItem(SVATO_SEEN_KEY) || '[]').filter((h) => typeof h === 'string'); } catch (e) { return []; }
  },
  add(handle) {
    if (!handle) return;
    try {
      const list = [handle, ...svatoSeen.read().filter((h) => h !== handle)].slice(0, 20);
      localStorage.setItem(SVATO_SEEN_KEY, JSON.stringify(list));
    } catch (e) { /* navigation privée : on ne mémorise rien */ }
  },
};

class SvatoRecent extends HTMLElement {
  async connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';
    this.track = this.querySelector('[data-rv-track]');
    this.controls = this.querySelector('[data-rv-controls]');
    this.prev = this.querySelector('[data-rv-prev]');
    this.next = this.querySelector('[data-rv-next]');
    this.bar = this.querySelector('[data-rv-progress]');
    this.thumb = this.querySelector('[data-rv-thumb]');
    const current = this.dataset.current;
    const limit = Number(this.dataset.limit || 10);
    const source = this.dataset.source || 'recent_then_collection';

    // Historique lu AVANT d'ajouter la fiche courante.
    let handles = source === 'collection' ? [] : svatoSeen.read();
    if (this.hasAttribute('data-exclude-current')) handles = handles.filter((h) => h !== current);
    handles = handles.slice(0, limit);
    if (this.hasAttribute('data-record') && !this.hasAttribute('data-design-mode')) svatoSeen.add(current);

    const items = [];
    const seen = new Set();
    const cards = await Promise.all(handles.map((h) => this.fetchCard(h)));
    cards.forEach((html, i) => {
      if (!html || seen.has(handles[i])) return;
      seen.add(handles[i]);
      const li = document.createElement('li');
      li.className = 'svato-pdp-recent__item' + (this.dataset.cardScheme ? ' color-' + this.dataset.cardScheme : '');
      li.dataset.handle = handles[i];
      li.innerHTML = html;
      items.push(li);
    });
    if (source !== 'recent') {
      const tpl = this.querySelector('template[data-rv-fallback]');
      [...(tpl?.content.children || [])].forEach((li) => {
        if (items.length >= limit || seen.has(li.dataset.handle)) return;
        seen.add(li.dataset.handle);
        items.push(li.cloneNode(true));
      });
    }
    if (items.length === 0) {
      if (this.hasAttribute('data-design-mode')) {
        this.hidden = false;
        this.track.innerHTML = '<li class="svato-pdp-recent__item" style="flex-basis:100%;opacity:.7">Aucun produit à afficher : choisissez une collection de repli (les produits vus récemment apparaissent ensuite chez chaque visiteur).</li>';
      }
      return;
    }
    this.track.replaceChildren(...items);
    this.hidden = false;
    this.bindSwatches();

    this.prev?.addEventListener('click', () => this.page(-1));
    this.next?.addEventListener('click', () => this.page(1));
    this.onScroll = () => { cancelAnimationFrame(this.raf); this.raf = requestAnimationFrame(() => this.update()); };
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    requestAnimationFrame(() => this.update());
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onScroll);
  }

  async fetchCard(handle) {
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(handle)}?view=svato-card`, { credentials: 'same-origin' });
      if (!res.ok) return null;
      const html = (await res.text()).trim();
      return html.includes('data-svato-card') ? html : null;
    } catch (e) {
      return null;
    }
  }

  // Une « page » = la largeur visible du rail.
  page(dir) {
    const first = this.track.children[0];
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const step = first ? first.offsetWidth + gap : this.track.clientWidth;
    const perView = Math.max(1, Math.floor((this.track.clientWidth + gap) / step));
    this.track.scrollBy({ left: dir * step * perView, behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  update() {
    const max = this.track.scrollWidth - this.track.clientWidth;
    const overflow = max > 2;
    if (this.controls) this.controls.hidden = !overflow;
    if (this.bar) this.bar.hidden = !overflow;
    if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
    if (this.next) this.next.disabled = this.track.scrollLeft >= max - 2;
    if (overflow && this.bar && this.thumb) {
      const ratio = this.track.clientWidth / this.track.scrollWidth;
      this.bar.style.setProperty('--rv-ratio', ratio.toFixed(4));
      const barWidth = this.bar.clientWidth - parseFloat(getComputedStyle(this.bar).paddingLeft) * 2;
      const shift = (barWidth - barWidth * ratio) * (this.track.scrollLeft / max);
      this.bar.style.setProperty('--rv-shift', `${shift}px`);
    }
  }

  // Pastille : change la photo et le lien de la carte, comme sur Koala.
  bindSwatches() {
    this.track.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-swatch]');
      if (!btn) return;
      e.preventDefault();
      const card = btn.closest('[data-svato-card]');
      card.querySelectorAll('[data-swatch]').forEach((b) => b.setAttribute('aria-pressed', b === btn ? 'true' : 'false'));
      const img = card.querySelector('[data-card-img]');
      if (img && btn.dataset.img) { img.srcset = ''; img.src = btn.dataset.img; }
      if (btn.dataset.url) card.querySelectorAll('[data-card-link]').forEach((a) => { a.href = btn.dataset.url; });
    });
  }
}
define('svato-recent', SvatoRecent);

/* ------------------------------------------------------------------ */
/* Liens internes de la fiche (note → avis) : défilement qui s'arrête  */
/* sous l'en-tête collant et la barre d'ancres.                        */
/* ------------------------------------------------------------------ */
if (!window.svatoScrollLinks) {
  window.svatoScrollLinks = true;
  document.addEventListener('click', (e) => {
    const a = e.target.closest('a[data-svato-scroll]');
    if (!a) return;
    const id = decodeURIComponent((a.getAttribute('href') || '').slice(1));
    const target = id && document.getElementById(id);
    if (!target) return;
    e.preventDefault();
    const header = document.querySelector('.section-header');
    let top = header && getComputedStyle(header).position === 'sticky' ? header.offsetHeight : 0;
    const bar = document.querySelector('svato-anchors[data-sticky]');
    if (bar && bar.offsetParent !== null) top += bar.offsetHeight;
    window.scrollTo({ top: Math.max(0, target.getBoundingClientRect().top + scrollY - top), behavior: reduceMotion ? 'auto' : 'smooth' });
    history.replaceState(null, '', '#' + id);
  });
}

/* Bouton « Réponse » qui ramène au bloc d'achat. */
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-svato-scroll-achat]');
  if (!btn) return;
  const cible = zoneAchat() || document.querySelector('.product-section');
  if (!cible) return;
  const y = cible.getBoundingClientRect().top + scrollY - 80;
  window.scrollTo({ top: Math.max(0, y), behavior: reduceMotion ? 'auto' : 'smooth' });
  document.querySelector('[data-ref="add-to-cart-button"]')?.focus({ preventScroll: true });
});
